/* eslint-disable no-restricted-syntax, arrow-body-style */

import { mapActions, mapGetters, mapState } from 'vuex';
import { dateToString, toCents, isMarketSingle } from '@/utils/helpers';
import moment from 'moment';
import ls from '@/helpers/localStorage';
import { MODALS } from '@/components/app/app-modals/app-modals';
import smallModal from '@/components/generics/modal/small/small.vue';
import loaderMixin from '@/mixins/loader';

export const CANCEL_ORDER_MODE = {
  ALL: 'all',
  SINGLE: 'single',
  NONE: 'none',
};


export const ORDER_BOOK_OFFER_DISPLAY_MODE = {
  BUY: 'buy',
  SELL: 'sell',
  TOGGLE: 'toggle',
};

export default {
  mixins: [loaderMixin],
  components: {
    smallModal,
  },
  props: {
    scrollable: {
      type: Boolean,
      default: false,
    },
    minimumRows: {
      type: Number,
      default: 0,
    },
    contract: {
      type: Object,
      required: true,
    },
    market: {
      type: Object,
      required: true,
    },
    buyOffers: {
      type: Array,
      required: true,
      default: [],
    },
    sellOffers: {
      type: Array,
      required: true,
      default: [],
    },
    displayMode: {
      type: String,
      required: false,
      default: ORDER_BOOK_OFFER_DISPLAY_MODE.TOGGLE
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    cardLayout: {
      type: Boolean,
      default: false,
    },
    compactCardLayout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      CANCEL_ORDER_MODE,
      ORDER_BOOK_OFFER_DISPLAY_MODE,
      showAll: this.scrollable,
      showAllCut: 5,
      deleteAllMode: false,
      deleteOfferId: null,
      showMenu: false,
      cancelModalMode: CANCEL_ORDER_MODE.NONE, // 'all', 'single'
      activeTab: null,
    };
  },
  watch: {
    'contract.contractId': {
      handler() {
        this.updateShowAll();
        this.updateActiveTab();
      },
    },
    activeTab(newTab) {
      if (newTab) {
        ls.set(`order_book_active_tab_${this.contract.contractId}`, newTab);
      }
    },
  },
  computed: {
    ...mapState('trade', ['tradeOptions']),
    ...mapGetters('app', ['isLayoutMobile', 'isCancelOrdersEnabled']),
    ...mapGetters('user', ['isLoggedIn']),
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    imageUrl() {
      return this.contract.contractImageSmallUrl || this.contract.contractImageUrl;
    },
    contractImageStyleModifiers() {
      return {
        'background-image': `url(${this.imageUrl})`,
      };
    },
    offersToDelete() {
      if (this.deleteAllMode) {
        return this.offers;
      }
      return this.offers.filter(o => o.offerId === this.deleteOfferId);
    },
    activeOfferSide() {
      if (this.displayMode === ORDER_BOOK_OFFER_DISPLAY_MODE.SELL) return 'sell';
      if (this.displayMode === ORDER_BOOK_OFFER_DISPLAY_MODE.TOGGLE) return this.activeTab;
      return 'buy';
    },
    emptyOrdersText() {
      return `You have no ${this.activeOfferSide} orders.`;
    },
    showCancelModal() {
      return this.cancelModalMode !== CANCEL_ORDER_MODE.NONE;
    },
    classModifiers() {
      return {
        'order-book-offers-sushi': true,
        'order-book-offers-sushi--card-layout': this.cardLayout,
        'order-book-offers-sushi--compact-card': this.cardLayout && this.compactCardLayout,
        'order-book-offers-sushi--card-empty': this.isCardEmpty,
        'order-book-offers-sushi--delete-all': this.deleteAllMode,
      };
    },
    tableModifiers() {
      return {
        'order-book-sushi__table': true,
        'order-book-sushi__table--scrollable': this.scrollable,
      };
    },
    offers() {
      const buyOffersMapped = this.buyOffers.map((o) => {
        return {
          ...o,
          pricePerShare: toCents(o.pricePerShare),
        };
      });
      const sellOffersMapped = this.sellOffers.map((o) => {
        return {
          ...o,
          pricePerShare: toCents(o.pricePerShare),
        };
      });
      buyOffersMapped.sort((prev, next) => {
        if (prev.pricePerShare === next.pricePerShare) {
          return 0;
        }
        let result = 1;
        if (prev.pricePerShare < next.pricePerShare) result = -1;

        return -result;
      });
      sellOffersMapped.sort((prev, next) => {
        if (prev.pricePerShare === next.pricePerShare) {
          return 0;
        }
        let result = 1;
        if (prev.pricePerShare < next.pricePerShare) result = -1;

        return result;
      });

      if (this.displayMode === ORDER_BOOK_OFFER_DISPLAY_MODE.BUY) {
        return buyOffersMapped;
      }
      if (this.displayMode === ORDER_BOOK_OFFER_DISPLAY_MODE.SELL) {
        return sellOffersMapped;
      }
      if (this.displayMode === ORDER_BOOK_OFFER_DISPLAY_MODE.TOGGLE) {
        return this.activeTab === ORDER_BOOK_OFFER_DISPLAY_MODE.BUY ? buyOffersMapped : sellOffersMapped;
      }

      return [...buyOffersMapped, ...sellOffersMapped];
    },
    showAllItemsNotice() {
      if (this.scrollable) return false;
      return this.offers.length > this.showAllCut;
    },
    itemsToDisplay() {
      return this.showAll ? this.offers : this.offers.slice(0, this.showAllCut)
    },
    displayRows() {
      const displayRows = [...this.itemsToDisplay];
      let minimumRows = this.minimumRows;
      if (this.cardLayout && this.compactCardLayout) {
        minimumRows = this.isCardEmpty ? 1 : 0;
      }
      const rowCount = Math.max(displayRows.length, minimumRows);

      while (displayRows.length < rowCount) {
        displayRows.push(null);
      }

      return displayRows;
    },
    isCardEmpty() {
      return this.cardLayout && this.offers.length === 0;
    },
    showProcessedNotice() {
      for (const item of this.offers) {
        if (!item.isProcessed) return true;
      }
      return false;
    },
  },
  methods: {
    ...mapActions('market', ['getMarket']),
    ...mapActions('trade', [
      'cancelOffer', 'cancelAllOffers', 'getContractOffers', 'getOrderBook',
      'setTradeOptions',
    ]),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentSmallPopup', 'setCurrentPopupOptions']),
    ...mapActions('notifications', ['setSuccessSideNotification']),
    getDate(item) {
      const v = dateToString(item.dateCreated).split(' ');
      return v[0];
    },
    getDateFull(item) {
      return dateToString(item.dateCreated);
    },
    onClickInner() {

    },
    onClickOutside() {
      this.showMenu = false;
    },
    rowModifiers(item) {
      return {
        'order-book-sushi__row': true,
        'order-book-sushi__row--striped': true,
        'order-book-offers-sushi__row--to-cancel': (item.offerId === this.deleteOfferId) || this.deleteAllMode,
      };
    },
    rowTitle(item) {
      return `Created ${moment(item.dateCreated).format('MM/DD/YYYY h:mm a')} ET - Original Quantity: ${item.quantity}`;
    },
    loginClicked() {
      this.setCurrentPopup(MODALS.LOGIN);
    },
    rowClicked(item) {
      if (!this.isLayoutMobile) return;
      this.setCurrentSmallPopup(MODALS.SMALL_OFFER_INFO);
      this.setCurrentPopupOptions({
        title: this.rowTitle(item),
      });
    },
    offerInfoDisplay(item) {
      switch (item.tradeType) {
        case 0: return 'Buy NO';
        case 1: return 'Buy YES';
        case 2: return 'Sell NO';
        case 3: return 'Sell YES';
        default: return '';
      }
    },
    offerFullInfoDisplay(item) {
      const quantity = item.remainingQuantity.toLocaleString();
      const price = `${item.pricePerShare}&cent;`;
      switch (item.tradeType) {
        case 0: return `Buy ${quantity} NO at Max ${price}`;
        case 1: return `Buy ${quantity} YES at Max ${price}`;
        case 2: return `Sell ${quantity} NO at Min ${price}`;
        case 3: return `Sell ${quantity} YES at Min ${price}`;
        default: return '';
      }
    },
    deleteClicked(offerId) {
      this.cancelClicked();
      this.deleteOfferId = offerId;
      this.cancelModalMode = CANCEL_ORDER_MODE.SINGLE;
    },
    confirmDeleteClicked(offerId) {
      if (!this.isCancelOrdersEnabled) {
        // this.setCurrentSmallPopup(MODALS.SMALL_PROCESS_ERROR);
        this.setCurrentPopupOptions({ message: 'The site is in Maintenance so we are unable to cancel your order at this time. Please try again later.' });
      } else {
        this.loaderAdd(document.body);
        this.cancelOffer(offerId).then(
          () => {
            this.setSuccessSideNotification('Your order has been removed.');
            this.getContractOffers(this.contract.contractId);
            this.cancelClicked();
            this.$emit('offerCanceled');
          },
        ).catch(() => this.cancelClicked());
      }
    },
    deleteAllClicked() {
      this.cancelClicked();
      this.deleteAllMode = true;
      this.showMenu = false;
      this.cancelModalMode = CANCEL_ORDER_MODE.ALL;
    },
    confirmDeleteAllClicked() {
      if (!this.isCancelOrdersEnabled) {
        // this.setCurrentSmallPopup(MODALS.SMALL_PROCESS_ERROR);
        this.setCurrentPopupOptions({ message: 'The site is in Maintenance so we are unable to cancel your order at this time. Please try again later.' });
      } else {
        const payload = {
          contractId: this.contract.contractId,
          offerType: this.activeOfferSide,
        };
        this.loaderAdd(document.body);
        this.cancelAllOffers(payload).then(
          () => {
            this.setSuccessSideNotification('Your orders have been removed.');
            this.getContractOffers(this.contract.contractId);
            this.cancelClicked();
            this.$emit('offerCanceled');
          },
        ).catch(() => this.cancelClicked());
      }
    },
    cancelClicked() {
      this.cancelModalMode = CANCEL_ORDER_MODE.NONE;
      this.showMenu = false;
      this.deleteAllMode = false;
      this.deleteOfferId = null;
      this.loaderRemove(document.body);
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
      ls.set(`order_book_all_offers_show_all_${this.contract.contractId}`, this.showAll);
    },
    updateShowAll() {
      if (this.scrollable) return;
      this.showAll = ls.get(`order_book_all_offers_show_all_${this.contract.contractId}`, false);
    },
    updateActiveTab() {
      this.activeTab = ls.get(`order_book_active_tab_${this.contract.contractId}`, ORDER_BOOK_OFFER_DISPLAY_MODE.BUY);
    },
  },
  created() {
    this.cancelClicked();
    this.updateShowAll();
    this.updateActiveTab();
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-offers-sushi/order-book-offers-sushi.js